import { initializeApp, FirebaseApp } from 'firebase/app'
import type { Analytics } from 'firebase/analytics'
import { FIREBASE_CONFIG, USE_FIREBASE } from '../../Config/Firebase/config'
import {
  initializeAuth,
  indexedDBLocalPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
  GoogleAuthProvider,
  onAuthStateChanged,
} from 'firebase/auth'
import type { Auth } from 'firebase/auth'

export type GCredential = {
  clientId: string
  credential: string
  select_by: string
}

let firebaseApp: FirebaseApp | undefined = undefined
let auth: Auth | undefined = undefined
export let analytics: any | undefined = undefined
export let googleProvider: any | undefined = undefined

let authStateReadyPromise: Promise<void> | null = null
let isAuthStateReady = false

export async function initFirebase() {
  if (firebaseApp !== undefined) {
    return
  }

  if (USE_FIREBASE) {
    firebaseApp = initializeApp(FIREBASE_CONFIG)
    auth = initializeAuth(firebaseApp, {
      persistence: [
        indexedDBLocalPersistence,
        browserLocalPersistence,
        browserSessionPersistence,
      ],
    })
      try {
        const analyticsModule = await import(
          /* webpackChunkName: "firebase-analytics" */ 'firebase/analytics'
        )
        if (!analyticsModule?.isSupported()) {
          console.warn('Analytics is not supported')
          return
        }
        const analyticsFn = analyticsModule.getAnalytics
        if(!analyticsFn) {
          console.warn('Analytics function not found')
          return
        }
        analytics = analyticsFn(firebaseApp)
      } catch (error) {
        console.warn('Failed to load Firebase Analytics:', error)
      }

    if (!authStateReadyPromise) {
      authStateReadyPromise = initializeAuthStateReady()
    }
  }
}

/**
 * Initialize the auth state ready promise that resolves when the initial auth state is determined
 */
async function initializeAuthStateReady(): Promise<void> {
  return new Promise((resolve) => {
    if (!auth) {
      isAuthStateReady = true
      resolve()
      return
    }

    let hasResolved = false
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!hasResolved) {
        hasResolved = true
        isAuthStateReady = true
        resolve()
        unsubscribe()
      }
    })

    setTimeout(() => {
      if (!hasResolved) {
        console.warn('Auth state ready timeout - proceeding anyway')
        hasResolved = true
        isAuthStateReady = true
        resolve()
        unsubscribe()
      }
    }, 10000) // 10 second timeout
  })
}

/**
 * Wait for Firebase auth state to be ready before proceeding
 */
export async function waitForAuthStateReady(): Promise<void> {
  if (isAuthStateReady) {
    return Promise.resolve()
  }

  if (!authStateReadyPromise) {
    await initFirebase()
  }

  if (authStateReadyPromise) {
    await authStateReadyPromise
  }
}

export async function getFirAuth(): Promise<Auth | undefined> {
  await waitForAuthStateReady()
  if (auth === undefined) {
    throw new Error('no auth access')
  }
  return auth
}

export async function getGoogleAuthCredential(cred: string): Promise<any | undefined> {
  return GoogleAuthProvider.credential(cred)
}

export async function getFirAnalytics(): Promise<Analytics> {
  if (analytics === undefined) {
    await initFirebase()
  }
  if (analytics === undefined) {
    throw new Error('Firebase Analytics not available')
  }
  return analytics
}

export const getGoogleProvider = async () => {
  if (googleProvider === undefined) {
    googleProvider = new GoogleAuthProvider()
  }
  return googleProvider
}

export const getActionCodeSettings = (redirect: string) => {
  return {
    url: redirect,
    handleCodeInApp: true,
    iOS: {
      bundleId: process.env.REACT_APP_IOS_BUNDLE_ID || 'com.pll.PLL',
    },
    android: {
      packageName: process.env.REACT_APP_ANDROID_BUNDLE_ID || 'com.pll.pllandroid',
      installApp: true,
    },
  }
}

/**
 * Reset auth state ready flag - useful for testing or error recovery
 */
export const resetAuthStateReady = () => {
  isAuthStateReady = false
  authStateReadyPromise = null
}
